// src/lib/incidents-attente.js
//
// DEPUIS QUAND UN INCIDENT ATTEND-IL D'ÊTRE JUGÉ ? (demande 2026-08-06)
//
// L'onglet Incidents de l'orchestrateur affichait un compte — « À vérifier (4) »
// — et rien d'autre. Un signalement déposé ce matin et un qui traîne depuis
// trois semaines s'y ressemblaient exactement, et l'ordre d'affichage mettait
// le plus RÉCENT en tête : le plus vieux, donc le plus urgent, finissait en bas
// de liste, là où on ne regarde plus.
//
// Or juger un incident n'est pas neutre : tant que personne ne tranche,
// l'agent ne sait pas si sa déclaration a été prise au sérieux, et rien ne part
// vers le rachat. L'ancienneté EST l'information.
//
// Helpers PURS : aucun accès réseau, aucun DOM, aucune horloge interne — la
// date du jour est INJECTÉE, pour que ces fonctions soient testables sans
// dépendre du moment où les tests tournent.

// Seuil au-delà duquel une attente devient anormale. Trois jours : un
// orchestrateur peut être en congé un week-end sans que tout vire au rouge, et
// au-delà l'incident a manifestement échappé à la boucle.
export const SEUIL_ATTENTE_JOURS = 3

/** Date ISO (YYYY-MM-DD) d'un incident : le CONSTAT prime sur le signalement. */
function dateIncident(inc) {
  const d = inc?.incident_date || inc?.reported_at || null
  if (typeof d !== 'string' || d.length < 10) return null
  const iso = d.slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null
}

/**
 * Nombre de jours d'attente. Jamais négatif : un incident daté du futur (saisie
 * erronée, fuseau) compte 0 — afficher « -2 jours » ferait douter de tout
 * l'écran.
 *
 * @param {object} inc
 * @param {string} todayIso — aujourd'hui, Europe/Paris (injecté).
 * @returns {number|null} null si la date est inexploitable.
 */
export function joursDAttente(inc, todayIso) {
  const iso = dateIncident(inc)
  if (!iso || typeof todayIso !== 'string') return null
  const a = Date.parse(`${iso}T12:00:00Z`)
  const b = Date.parse(`${todayIso}T12:00:00Z`)
  if (Number.isNaN(a) || Number.isNaN(b)) return null
  return Math.max(0, Math.round((b - a) / 86400000))
}

/** L'attente dépasse-t-elle le seuil ? Une date inexploitable ⇒ jamais d'alerte. */
export function attenteAnormale(inc, todayIso, seuil = SEUIL_ATTENTE_JOURS) {
  const j = joursDAttente(inc, todayIso)
  return j != null && j > seuil
}

/**
 * Trie les incidents du PLUS ANCIEN au plus récent — l'inverse de l'ordre
 * naturel d'un journal, et c'est voulu : sur une file d'attente de jugement, le
 * plus vieux est le plus urgent. Les dates inexploitables vont en fin de liste
 * plutôt que d'être perdues (on ne masque jamais un incident).
 *
 * Tri STABLE et non destructif : l'entrée n'est pas mutée.
 */
export function trierParAnciennete(incidents) {
  const liste = Array.isArray(incidents) ? [...incidents] : []
  return liste
    .map((inc, i) => ({ inc, i, d: dateIncident(inc) }))
    .sort((x, y) => {
      if (x.d && y.d) return x.d === y.d ? x.i - y.i : (x.d < y.d ? -1 : 1)
      if (x.d) return -1
      if (y.d) return 1
      return x.i - y.i
    })
    .map((x) => x.inc)
}

/**
 * Phrase de contrôle en tête d'onglet — même grammaire que « Aujourd'hui » du
 * Suivi équipe : le sous-titre dit d'emblée s'il y a quelque chose à faire.
 *
 * @returns {{ texte: string, alerte: boolean }}
 */
export function resumeAttente(incidents, todayIso, seuil = SEUIL_ATTENTE_JOURS) {
  const liste = Array.isArray(incidents) ? incidents : []
  // « À JUGER » — un état, un mot (règle du dessin unique appliquée au
  // langage, Dimitri 2026-08-21). L'ancien « à vérifier » désignait le même état.
  if (!liste.length) return { texte: 'Rien à juger.', alerte: false }

  const n = liste.length
  const jours = liste.map((i) => joursDAttente(i, todayIso)).filter((j) => j != null)
  const plusVieux = jours.length ? Math.max(...jours) : null

  const debut = `${n} incident${n > 1 ? 's' : ''} à juger`
  if (plusVieux == null) return { texte: `${debut}.`, alerte: false }
  if (plusVieux > seuil) {
    return {
      texte: `${debut} · le plus ancien attend depuis ${plusVieux} jours`,
      alerte: true,
    }
  }
  return { texte: `${debut} · le plus ancien depuis ${plusVieux} j`, alerte: false }
}
